import { ApplicationConfig, ErrorHandler, importProvidersFrom } from '@angular/core';
import {
  provideRouter,
  withComponentInputBinding,
} from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { APP_INITIALIZER } from '@angular/core';
import { ToastrModule } from 'ngx-toastr';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeuix/themes/aura';
import { NgxMaskModule } from 'ngx-mask';
import { KeycloakService } from 'keycloak-angular';

import { interceptorProviders } from './core/interceptors';
import { routes } from './app.routes';
import { ToastrComponent } from './shared/components/toastr/toastr.component';
import { AppConfigService } from './core/services/app-config.service';
import { ElxKeycloakService } from './core/services/keycloak.service';
import { ConfirmationService, MessageService } from 'primeng/api';
import { initializeKeycloak } from './core/config/keycloak.config';
import { GlobalErrorHandler } from './core/errors/global-error-handler';

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: ErrorHandler, useClass: GlobalErrorHandler },

    // Router configuration with view transitions
    provideRouter(routes, withComponentInputBinding()),

    // Animations
    provideAnimations(),

    // Import providers from modules that need to remain modules
    importProvidersFrom(NgxMaskModule.forRoot()),
    {
      provide: APP_INITIALIZER,
      multi: true,
      deps: [
        AppConfigService,
        KeycloakService,
        ElxKeycloakService,
      ],
      useFactory:
        (
          appConfig: AppConfigService,
          keycloak: KeycloakService,
          elxKeycloak: ElxKeycloakService,
        ) =>
        async () => {
          const withTimeout = async <T>(
            label: string,
            task: Promise<T>,
            timeoutMs: number,
            onTimeout: () => Promise<void> | void,
          ): Promise<T | undefined> => {
            let timeoutHandle: number | undefined;
            const timeoutPromise = new Promise<undefined>((resolve) => {
              timeoutHandle = window.setTimeout(async () => {
                try {
                  await onTimeout();
                } finally {
                  resolve(undefined);
                }
              }, timeoutMs);
            });
            try {
              const result = await Promise.race([task, timeoutPromise]);
              return result as T | undefined;
            } finally {
              if (timeoutHandle !== undefined) {
                window.clearTimeout(timeoutHandle);
              }
            }
          };

          // 1. Load Application Configuration
          (window as any).__ELX_BOOT_STAGE__ = 'Loading runtime config…';
          await withTimeout(
            'appConfig.loadConfig',
            appConfig.loadConfig(),
            10000,
            async () => {
              try {
                                appConfig.setFallbackConfig({ envName: 'dev', releaseId: 'unknown' });
              } catch {}
            },
          );

          // 2. Initialize Keycloak (Auth/Tokens)
          // Robustness: avoid app-wide hang if Keycloak init stalls due to cached state/network.
          (window as any).__ELX_BOOT_STAGE__ = 'Initializing authentication…';
          await withTimeout(
            'keycloak.init',
            initializeKeycloak(keycloak, appConfig, elxKeycloak)(),
            18000,
            async () => {
              try {
                // Preserve the active session when possible. Keycloak's
                // silent-check-sso iframe is a frequent source of slow init
                // on production networks; clearing tokens here was logging
                // users out of perfectly valid sessions (guideline #5
                // Robustness — never destroy state on transient failures).
                const hasValidSession = elxKeycloak.hasValidPersistedToken();
                if (hasValidSession) {
                                    // Drop only the *adapter* state (PKCE/callback artifacts);
                  // app cookies/tokens stay intact so the user stays signed in.
                  elxKeycloak.clearKeycloakAdapterLocalState();
                  (window as any).__keycloakInitCompleted = true;
                  elxKeycloak.markInitialized(true);
                  // Kick off the silent refresh loop so we keep the token
                  // fresh even though `keycloak.init` never resolved.
                  try {
                    elxKeycloak.startAutomaticTokenRefresh();
                  } catch {}
                  return;
                }

                                elxKeycloak.clearKeycloakAdapterLocalState();
                elxKeycloak.clearPersistedTokens();
                (window as any).__keycloakInitCompleted = true;
                elxKeycloak.markInitialized(false);
              } catch {}
            },
          );

          if (typeof window !== 'undefined' && !(window as any).__keycloakInitCompleted) {
            (window as any).__keycloakInitCompleted = true;
            elxKeycloak.markInitialized(elxKeycloak.hasValidPersistedToken());
          }

          (window as any).__ELX_BOOT_STAGE__ = 'Finishing startup…';
        },
    },

    // Custom providers
    ...interceptorProviders,

    // PrimeNG configuration with Aura light theme
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          darkModeSelector: 'none', // Disable dark mode
        },
      },
    }),

    // Toastr configuration
    importProvidersFrom(
      ToastrModule.forRoot({
        timeOut: 10000, // 10 seconds for better visibility
        positionClass: 'toast-top-right',
        preventDuplicates: true,
        toastComponent: ToastrComponent,
        closeButton: true,
        progressBar: true,
        enableHtml: true,
      }),
    ),
    ConfirmationService,
    MessageService,
    KeycloakService,
  ],
};
